import dagre from '@dagrejs/dagre';
import { Edge, Node, Position } from 'reactflow';

import { AccountabilityChartNode } from 'types/api/Tenant/AccountabilityTypes';

export const ACCOUNTABILITY_CHART_NODE_SIZE = {
  width: 242,
  height: 96,
};

const GROUP_HORIZONTAL_PADDING = 18;
const GROUP_HEADER_HEIGHT = 32;
const GROUP_CONTENT_TOP_GAP = 14;
const GROUP_BOTTOM_PADDING = 16;
const GROUP_GAP = 18;

const NODE_GAP = GROUP_HEADER_HEIGHT + GROUP_CONTENT_TOP_GAP + GROUP_BOTTOM_PADDING + GROUP_GAP;
const RANK_GAP = 72;

export interface AccountabilityFlowNodeData {
  accountability: AccountabilityChartNode;
  directReportCount: number;
  hasChildren: boolean;
  isCollapsed: boolean;
  isRoot: boolean;
  showDirectReportCount?: boolean;
  onToggle?: (nodeId: string) => void;
}

export interface AccountabilityFlowElements {
  nodes: Node<AccountabilityFlowNodeData>[];
  edges: Edge[];
  groups: AccountabilityFlowGroup[];
}

export interface AccountabilityFlowGroup {
  id: string;
  label: string;
  parentNodeId: string;
  position: { x: number; y: number };
  width: number;
  height: number;
}

function getStableNodeId(node: AccountabilityChartNode, path: number[], usedIds: Set<string>) {
  const fallbackId = `accountability-node-${path.join('-') || 'root'}`;
  const preferredId = node.ktreeMemberId?.trim() || fallbackId;
  const id = usedIds.has(preferredId) ? `${preferredId}-${path.join('-')}` : preferredId;

  usedIds.add(id);
  return id;
}

function buildFlowElements(
  root: AccountabilityChartNode,
  collapsedNodeIds: ReadonlySet<string>,
): Pick<AccountabilityFlowElements, 'nodes' | 'edges'> {
  const nodes: Node<AccountabilityFlowNodeData>[] = [];
  const edges: Edge[] = [];
  const usedIds = new Set<string>();

  const visit = (node: AccountabilityChartNode, parentId: string | null, path: number[]) => {
    const id = getStableNodeId(node, path, usedIds);
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const isCollapsed = hasChildren && collapsedNodeIds.has(id);

    nodes.push({
      id,
      type: 'accountability',
      position: { x: 0, y: 0 },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        accountability: node,
        directReportCount: children.length,
        hasChildren,
        isCollapsed,
        isRoot: parentId === null,
      },
    });

    if (parentId) {
      edges.push({
        id: `accountability-edge-${parentId}-${id}`,
        source: parentId,
        target: id,
        type: 'smoothstep',
      });
    }

    if (!isCollapsed) {
      children.forEach((child, index) => visit(child, id, [...path, index]));
    }
  };

  visit(root, null, []);
  return { nodes, edges };
}

export function layoutAccountabilityChart(
  root?: AccountabilityChartNode | null,
  collapsedNodeIds: ReadonlySet<string> = new Set(),
): AccountabilityFlowElements {
  if (!root) return { nodes: [], edges: [], groups: [] };

  const elements = buildFlowElements(root, collapsedNodeIds);
  const graph = new dagre.graphlib.Graph();

  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: 'LR',
    ranker: 'tight-tree',
    nodesep: NODE_GAP,
    ranksep: RANK_GAP,
    marginx: 24,
    marginy: 24,
  });

  elements.nodes.forEach((node) => {
    graph.setNode(node.id, { ...ACCOUNTABILITY_CHART_NODE_SIZE });
  });
  elements.edges.forEach((edge) => {
    graph.setEdge(edge.source, edge.target);
  });

  dagre.layout(graph);

  const positionedNodes = elements.nodes.map((node) => {
    const layoutNode = graph.node(node.id);

    if (!layoutNode) return node;

    return {
      ...node,
      position: {
        x: layoutNode.x - ACCOUNTABILITY_CHART_NODE_SIZE.width / 2,
        y: layoutNode.y - ACCOUNTABILITY_CHART_NODE_SIZE.height / 2,
      },
    };
  });
  const nodesById = new Map(positionedNodes.map((node) => [node.id, node]));
  const childIdsByParent = new Map<string, string[]>();

  elements.edges.forEach((edge) => {
    const childIds = childIdsByParent.get(edge.source) ?? [];
    childIds.push(edge.target);
    childIdsByParent.set(edge.source, childIds);
  });

  const groups: AccountabilityFlowGroup[] = [];
  childIdsByParent.forEach((childIds, parentNodeId) => {
    const parentNode = nodesById.get(parentNodeId);
    const childNodes = childIds
      .map((childId) => nodesById.get(childId))
      .filter(Boolean) as Node<AccountabilityFlowNodeData>[];

    if (!parentNode || childNodes.length === 0) return;

    const minX = Math.min(...childNodes.map((node) => node.position.x));
    const minY = Math.min(...childNodes.map((node) => node.position.y));
    const maxX = Math.max(...childNodes.map((node) => node.position.x + ACCOUNTABILITY_CHART_NODE_SIZE.width));
    const maxY = Math.max(...childNodes.map((node) => node.position.y + ACCOUNTABILITY_CHART_NODE_SIZE.height));
    const label = parentNode.data.accountability.member?.trim() || 'Unnamed member';

    groups.push({
      id: `accountability-group-${parentNodeId}`,
      label,
      parentNodeId,
      position: {
        x: minX - GROUP_HORIZONTAL_PADDING,
        y: minY - GROUP_HEADER_HEIGHT - GROUP_CONTENT_TOP_GAP,
      },
      width: maxX - minX + GROUP_HORIZONTAL_PADDING * 2,
      height: maxY - minY + GROUP_HEADER_HEIGHT + GROUP_CONTENT_TOP_GAP + GROUP_BOTTOM_PADDING,
    });
  });

  return {
    edges: elements.edges,
    nodes: positionedNodes,
    groups,
  };
}
